import type { ClipboardEvent } from 'react'
import { Editable, Slate, withReact } from 'slate-react'
import { Editor, createEditor } from 'slate'
import { forwardRef, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { withHistory } from 'slate-history'

import {
  ToolbarContext,
  initialToolbarState,
  toolbarReducer,
} from '../templateComponents/Workspace/plugins/text/RichTextEditor'
import type { ToolbarContextType } from '../templateComponents/Workspace/plugins/text/RichTextEditor'
import { parseHtml, serialize, withPasting } from '../templateComponents/Workspace/plugins/text/serialization'
import { useTranslate } from '../../utils/translate'
import { withLinks } from '../templateComponents/Workspace/plugins/text/links'
import { withSingleLine, withUtils } from '../templateComponents/Workspace/plugins/text/utils'
import Element from '../templateComponents/Workspace/plugins/text/Element'
import Leaf from '../templateComponents/Workspace/plugins/text/Leaf'
import LinkButton from '../templateComponents/Workspace/plugins/text/toolbar/LinkButton'
import MarkButton from '../templateComponents/Workspace/plugins/text/toolbar/MarkButton'
import TextLinkTooltip from '../templateComponents/Workspace/plugins/components/LinkTooltip/LinkTooltipText'
import TooltipStateProvider from '../templateComponents/Workspace/plugins/components/LinkTooltip/LinkTooltipContext'

type Props = {
  readonly value: string
  readonly onChange: (htmlValue: string) => void
  readonly toolbarButtons?: Array<'bold' | 'link|unlink'>
  readonly placeholderText?: string
  readonly restrictToSingleLine?: boolean
  readonly maxCharacters?: number
}

// Note that this component shares the same translation namespace as the
// `RichTextEditor` component. If a key is renamed, it needs to be updated in
// both components.
export const RichTextField = forwardRef<HTMLElement, Props>(
  (
    { value: html, onChange: onHtmlChange, toolbarButtons = [], placeholderText, restrictToSingleLine, maxCharacters },
    _ref,
  ) => {
    const t = useTranslate('interface', 'components.richTextComponent')
    const editableRef = useRef<HTMLDivElement>(null)
    const [value, setValue] = useState(parseHtml(html))
    const [textCount, setTextCount] = useState(0)

    const editor = useMemo(() => {
      const createdEditor = withUtils(withHistory(withLinks(withPasting(withReact(createEditor())))))
      if (restrictToSingleLine) withSingleLine(createdEditor)
      return createdEditor
    }, [restrictToSingleLine])

    // Make sure that the text count is updated on initial render.
    // Necessary because setting Editor.string(editor, []).length as initial state would throw a slate error.
    useEffect(() => {
      const textLength = Editor.string(editor, []).length
      setTextCount(textLength)
    }, [editor])

    useEffect(() => {
      const newHtml = serialize(value)
      if (newHtml !== html) onHtmlChange(newHtml)
    }, [html, onHtmlChange, value])

    const renderElement = useCallback((props) => <Element {...props} />, [])
    const renderLeaf = useCallback((props) => <Leaf {...props} />, [])
    const updateValue = useCallback(
      (value) => {
        const textLength = Editor.string(editor, []).length
        setValue([...value])
        setTextCount(textLength)
      },
      [editor],
    )

    useEffect(() => {
      // Triggering normalization on initial value.
      // See: https://github.com/ianstormtaylor/slate/issues/3465#issuecomment-655592962
      // Also prevents this bug: https://epages.atlassian.net/browse/EPUI-1713
      editor.children = value
      Editor.normalize(editor, { force: true })
      setValue(editor.children)
    }, [value, editor])

    const [toolbarState, toolbarDispatch] = useReducer(toolbarReducer, initialToolbarState)
    const toolbarContext = useMemo<ToolbarContextType>(() => [toolbarState, toolbarDispatch], [toolbarState])

    const selectedText = editor.selection
    const selectedTextLength = selectedText ? Editor.string(editor, selectedText).length : 0
    const unselectedTextLength = textCount - selectedTextLength

    const limitTypingToMaxCharacters = (event: InputEvent) => {
      const inputType = event.inputType

      if (maxCharacters && inputType === 'insertText') {
        const newInputLength = event.data?.length || 0
        if (unselectedTextLength + newInputLength > maxCharacters) {
          event.preventDefault()
        }
      }
    }

    const limitPastingToMaxCharacters = (event: ClipboardEvent) => {
      if (maxCharacters) {
        event.preventDefault()
        const pastedText = event.clipboardData.getData('text')
        const pastedTextLength = pastedText.length
        if (unselectedTextLength + pastedTextLength > maxCharacters) {
          const allowedText = pastedText.substring(0, maxCharacters - unselectedTextLength)
          Editor.insertText(editor, allowedText)
        } else {
          Editor.insertText(editor, pastedText)
        }
      }
    }

    return (
      <>
        {maxCharacters && (
          <span className="ep-form-row-label-counter">
            {t('characterCounter', { length: textCount, limit: maxCharacters })}
          </span>
        )}
        <ToolbarContext.Provider value={toolbarContext}>
          <div className="ep-text-editor" tabIndex={-1}>
            <Slate editor={editor} initialValue={value} onChange={updateValue}>
              {toolbarButtons.length > 0 && (
                <div className="ep-text-editor-toolbar">
                  <div className="ep-text-editor-toolbar-button-wrapper">
                    {toolbarButtons.map((button) => {
                      return (
                        <div key={button}>
                          {button === 'bold' && <MarkButton title={t('boldButton.tooltip')} format="bold" />}
                          {button === 'link|unlink' && (
                            <>
                              <LinkButton title={t('linkButton.tooltip')} format="link" />
                              <LinkButton title={t('removeHyperlinkButton.tooltip')} format="unlink" />
                            </>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
              <div ref={editableRef}>
                <Editable
                  className="ep-text-editor-main"
                  renderLeaf={renderLeaf}
                  renderElement={renderElement}
                  // Note that if the placeholder key is renamed, it also needs to
                  // be updated in the `RichTextEditor` component (see note above).
                  placeholder={placeholderText ?? t('textInputField.standardPlaceholder')}
                  onDOMBeforeInput={limitTypingToMaxCharacters}
                  onPaste={limitPastingToMaxCharacters}
                />
                <TooltipStateProvider>
                  <TextLinkTooltip sideOffset={0} side="top" editableRef={editableRef} />
                </TooltipStateProvider>
              </div>
            </Slate>
          </div>
        </ToolbarContext.Provider>
      </>
    )
  },
)

RichTextField.displayName = 'RichTextField'
